<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InternalGroupsAPI from 'dashboard/api/internalGroups';
import {
  canRemoveInternalGroupMembers,
  internalParticipantIds,
} from 'dashboard/helper/internalGroupMembershipHelper';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  members: {
    type: Array,
    default: () => [],
  },
  groupName: {
    type: String,
    default: '',
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'updated']);

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const confirmDialogRef = ref(null);
const removingMemberId = ref(null);
const memberPendingRemoval = ref(null);
const showAddSection = ref(false);
const addSearchQuery = ref('');
const selectedAddIds = ref([]);
const isAddingMembers = ref(false);

const currentUser = useMapGetter('getCurrentUser');
const assignableAgents = useMapGetter(
  'inboxAssignableAgents/getAssignableAgents'
);
const assignableUiFlags = useMapGetter('inboxAssignableAgents/getUIFlags');

const canRemoveMembers = computed(() =>
  canRemoveInternalGroupMembers(props.conversation)
);

const currentMemberIds = computed(() =>
  internalParticipantIds(props.conversation)
);

const sortedMembers = computed(() => {
  const members = [...props.members];
  const currentUserId = currentUser.value?.id;

  return members.sort((a, b) => {
    if (a.id === currentUserId) return -1;
    if (b.id === currentUserId) return 1;

    const nameA = (a.name || a.available_name || '').toLowerCase();
    const nameB = (b.name || b.available_name || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });
});

const availableAgentsToAdd = computed(() => {
  const inboxAgents = assignableAgents.value(props.inboxId) || [];
  return inboxAgents.filter(agent => !currentMemberIds.value.includes(agent.id));
});

const filteredAgentsToAdd = computed(() => {
  const query = addSearchQuery.value.trim().toLowerCase();
  if (!query) return availableAgentsToAdd.value;

  return availableAgentsToAdd.value.filter(agent => {
    const name = (agent.name || agent.available_name || '').toLowerCase();
    const email = (agent.email || '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });
});

const canAddMembers = computed(() => selectedAddIds.value.length > 0);

function memberLabel(member) {
  if (member.id === currentUser.value?.id) {
    return t('CONVERSATION.INTERNAL_MESSAGING.YOU_LABEL');
  }

  return member.name || member.available_name;
}

function removeTooltip(member) {
  if (member.id === currentUser.value?.id) {
    return t('CONVERSATION.INTERNAL_MESSAGING.LEAVE_GROUP');
  }

  return t('CONVERSATION.INTERNAL_MESSAGING.REMOVE_MEMBER');
}

function canShowRemoveButton(member) {
  if (!canRemoveMembers.value) return false;

  return currentMemberIds.value.includes(member.id);
}

function toggleAddAgent(agentId) {
  if (selectedAddIds.value.includes(agentId)) {
    selectedAddIds.value = selectedAddIds.value.filter(id => id !== agentId);
    return;
  }

  selectedAddIds.value = [...selectedAddIds.value, agentId];
}

function resetAddForm() {
  showAddSection.value = false;
  addSearchQuery.value = '';
  selectedAddIds.value = [];
}

async function requestRemoveMember(member) {
  memberPendingRemoval.value = member;
  await nextTick();
  confirmDialogRef.value?.open();
}

function cancelRemoveMember() {
  memberPendingRemoval.value = null;
  confirmDialogRef.value?.close();
}

async function confirmRemoveMember() {
  const member = memberPendingRemoval.value;
  if (!member) return;

  removingMemberId.value = member.id;

  try {
    const { data: conversation } = await InternalGroupsAPI.removeMember({
      inboxId: props.inboxId,
      conversationId: props.conversationId,
      userId: member.id,
    });

    store.dispatch('updateConversation', conversation);
    emit('updated', conversation);
    memberPendingRemoval.value = null;

    const participantIds = internalParticipantIds(conversation);
    if (!participantIds.includes(currentUser.value?.id)) {
      emit('close');
    }
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.INTERNAL_MESSAGING.ERROR_REMOVE_MEMBER');
    useAlert(message);
  } finally {
    removingMemberId.value = null;
  }
}

async function onAddMembers() {
  if (!canAddMembers.value) return;

  isAddingMembers.value = true;

  try {
    const { data: conversation } = await InternalGroupsAPI.addMembers({
      inboxId: props.inboxId,
      conversationId: props.conversationId,
      participantIds: selectedAddIds.value,
    });

    store.dispatch('updateConversation', conversation);
    emit('updated', conversation);
    resetAddForm();
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.INTERNAL_MESSAGING.ERROR_ADD_MEMBERS');
    useAlert(message);
  } finally {
    isAddingMembers.value = false;
  }
}

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) {
      memberPendingRemoval.value = null;
      resetAddForm();
      dialogRef.value?.close();
      return;
    }

    dialogRef.value?.open();
    store.dispatch('inboxAssignableAgents/fetch', [Number(props.inboxId)]);
  }
);
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="alert"
    :title="$t('CONVERSATION.INTERNAL_MESSAGING.GROUP_MEMBERS_TITLE')"
    :description="groupName"
    :show-confirm-button="false"
    :cancel-button-label="$t('DIALOG.BUTTONS.CLOSE')"
    width="md"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="flex items-center justify-between mb-3">
      <p class="text-sm font-medium text-n-slate-12 m-0">
        {{ $t('CONVERSATION.INTERNAL_MESSAGING.CURRENT_MEMBERS') }}
      </p>
      <NextButton
        v-if="!showAddSection"
        xs
        slate
        ghost
        icon="i-lucide-user-plus"
        :label="$t('CONVERSATION.INTERNAL_MESSAGING.ADD_MEMBERS')"
        @click="showAddSection = true"
      />
    </div>

    <p
      v-if="!canRemoveMembers"
      class="text-xs text-n-slate-11 mb-2"
    >
      {{ $t('CONVERSATION.INTERNAL_MESSAGING.MIN_MEMBERS_HINT') }}
    </p>

    <ul
      v-if="sortedMembers.length"
      class="flex flex-col gap-1 max-h-56 overflow-y-auto mb-4"
    >
      <li
        v-for="member in sortedMembers"
        :key="member.id"
        class="flex items-center gap-3 px-3 py-2 rounded-lg"
      >
        <Avatar
          :name="member.name || member.available_name"
          :src="member.thumbnail"
          :status="member.availability_status"
          :size="32"
          rounded-full
        />
        <div class="flex flex-col min-w-0 flex-1">
          <span class="text-sm font-medium text-n-slate-12 truncate">
            {{ memberLabel(member) }}
          </span>
          <span
            v-if="member.email"
            class="text-xs text-n-slate-11 truncate"
          >
            {{ member.email }}
          </span>
        </div>
        <NextButton
          v-if="canShowRemoveButton(member)"
          v-tooltip.top="removeTooltip(member)"
          icon="i-lucide-user-minus"
          xs
          color="ruby"
          ghost
          :is-loading="removingMemberId === member.id"
          :disabled="removingMemberId && removingMemberId !== member.id"
          @click="requestRemoveMember(member)"
        />
      </li>
    </ul>
    <p
      v-else
      class="py-4 text-center text-n-slate-11 text-sm mb-4"
    >
      {{ $t('CONVERSATION.INTERNAL_MESSAGING.NO_GROUP_MEMBERS') }}
    </p>

    <div
      v-if="showAddSection"
      class="border-t border-n-weak pt-4 flex flex-col gap-3"
    >
      <div class="flex items-center justify-between">
        <p class="text-sm font-medium text-n-slate-12 m-0">
          {{ $t('CONVERSATION.INTERNAL_MESSAGING.ADD_MEMBERS_TITLE') }}
        </p>
        <NextButton
          xs
          slate
          ghost
          icon="i-lucide-x"
          @click="resetAddForm"
        />
      </div>

      <p class="text-xs text-n-slate-11 m-0">
        {{ $t('CONVERSATION.INTERNAL_MESSAGING.ADD_MEMBERS_HINT') }}
      </p>

      <input
        v-model="addSearchQuery"
        type="search"
        class="w-full"
        :placeholder="$t('CONVERSATION.INTERNAL_MESSAGING.SEARCH_AGENT')"
      />

      <div
        v-if="assignableUiFlags.isFetching"
        class="py-4 text-center text-n-slate-11 text-sm"
      >
        {{ $t('CONVERSATION.LOADING_CONVERSATIONS') }}
      </div>

      <p
        v-else-if="!filteredAgentsToAdd.length"
        class="py-4 text-center text-n-slate-11 text-sm"
      >
        {{ $t('CONVERSATION.INTERNAL_MESSAGING.NO_MEMBERS_TO_ADD') }}
      </p>

      <ul
        v-else
        class="flex flex-col gap-1 max-h-48 overflow-y-auto"
      >
        <li v-for="agent in filteredAgentsToAdd" :key="agent.id">
          <label
            class="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-n-alpha-1 cursor-pointer"
          >
            <input
              type="checkbox"
              class="!m-0"
              :checked="selectedAddIds.includes(agent.id)"
              @change="toggleAddAgent(agent.id)"
            />
            <Avatar
              :name="agent.name || agent.available_name"
              :src="agent.thumbnail"
              :size="32"
              rounded-full
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ agent.name || agent.available_name }}
            </span>
          </label>
        </li>
      </ul>

      <NextButton
        sm
        :label="$t('CONVERSATION.INTERNAL_MESSAGING.ADD_MEMBERS_BUTTON')"
        :disabled="!canAddMembers"
        :is-loading="isAddingMembers"
        @click="onAddMembers"
      />
    </div>
  </Dialog>

  <Dialog
    v-if="memberPendingRemoval"
    ref="confirmDialogRef"
    type="alert"
    :title="
      memberPendingRemoval.id === currentUser?.id
        ? $t('CONVERSATION.INTERNAL_MESSAGING.LEAVE_GROUP_TITLE')
        : $t('CONVERSATION.INTERNAL_MESSAGING.REMOVE_MEMBER_TITLE')
    "
    :description="
      memberPendingRemoval.id === currentUser?.id
        ? $t('CONVERSATION.INTERNAL_MESSAGING.LEAVE_GROUP_CONFIRM')
        : $t('CONVERSATION.INTERNAL_MESSAGING.REMOVE_MEMBER_CONFIRM', {
            name:
              memberPendingRemoval.name || memberPendingRemoval.available_name,
          })
    "
    :show-confirm-button="true"
    :confirm-button-label="
      memberPendingRemoval.id === currentUser?.id
        ? $t('CONVERSATION.INTERNAL_MESSAGING.LEAVE_GROUP_BUTTON')
        : $t('CONVERSATION.INTERNAL_MESSAGING.REMOVE_MEMBER_BUTTON')
    "
    :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
    width="sm"
    :is-loading="removingMemberId === memberPendingRemoval.id"
    @confirm="confirmRemoveMember"
    @close="cancelRemoveMember"
  />
</template>
